import React, { KeyboardEvent, memo, MouseEvent, TouchEvent, useCallback } from "react";

import { faEye } from "@fortawesome/pro-solid-svg-icons/faEye";
import { faEyeSlash } from "@fortawesome/pro-solid-svg-icons/faEyeSlash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Badge from "antd/es/badge";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import Color from "@mapmycustomers/shared/enum/Color";
import { Territory } from "@mapmycustomers/shared/types/entity";
import { isMacOS } from "@mapmycustomers/shared/util/browser";

import colors from "@app/styles/_colors.module.scss";
import anyColorToHex from "@app/util/colors/anyColorToHex";
import { VISIBLE_TERRITORIES_LIMIT } from "@app/util/consts";

import styles from "./TerritoryItem.module.scss";

type Event = KeyboardEvent | MouseEvent | TouchEvent;

interface Props {
  onClick?: (territory: Territory) => void;
  onMouseEnter?: (territory: Territory) => void;
  onMouseLeave?: (territory: Territory) => void;
  onVisibilityChange?: (
    territoryId: Territory["id"] | undefined,
    hide: boolean,
    exclusively: boolean
  ) => void;
  showingTerritoryDisabled?: boolean;
  territory: Territory;
}

const TerritoryItem: React.FC<Props> = ({
  onClick,
  onMouseEnter,
  onMouseLeave,
  onVisibilityChange,
  showingTerritoryDisabled,
  territory,
}) => {
  const intl = useIntl();

  const showDisabled = showingTerritoryDisabled && territory.territoryDetail.hidden;

  const handleToggleClick = useCallback(
    (e: Event) => {
      e.stopPropagation();
      if (showDisabled) {
        return;
      }

      if (e.shiftKey && !e.altKey) {
        // show all territories
        onVisibilityChange?.(undefined, false, false);
      } else if (e.altKey) {
        // hide all territories except current
        onVisibilityChange?.(territory.id, false, true);
      } else {
        // toggle current territory's visibility
        onVisibilityChange?.(territory.id, !territory.territoryDetail.hidden, false);
      }
    },
    [showDisabled, onVisibilityChange, territory]
  );

  const handleClick = useCallback(() => {
    onClick?.(territory);
  }, [onClick, territory]);

  const handleMouseEnter = useCallback(() => {
    onMouseEnter?.(territory);
  }, [onMouseEnter, territory]);

  const handleMouseLeave = useCallback(() => {
    onMouseLeave?.(territory);
  }, [onMouseLeave, territory]);

  return (
    <div
      className={cn(styles.container, { [styles.hidden]: territory.territoryDetail.hidden })}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Tooltip mouseEnterDelay={1} title={territory.name}>
        <Badge
          className={styles.title}
          color={
            territory.territoryDetail.hidden
              ? colors.accentGray
              : anyColorToHex(territory.color ?? Color.BLACK)
          }
          text={territory.name}
        />
      </Tooltip>
      <div className={styles.actions}>
        <Tooltip
          placement="right"
          title={intl.formatMessage(
            {
              id: "map.layer.boundaries.territories.legend.item.hideIcon.tooltip",
              defaultMessage:
                "{showDisabled, select, true {Only {limit} {limit, plural, one {territory} other {territories}} can be displayed at the same time.} other {{hidden, select, true {Show Territory{br}Or {isMac, select, true {⌥} other {alt}}+click to hide all but this territory} other {Hide Territory{br}Or {isMac, select, true {⌥} other {alt}}+click to hide all but this territory}}}}",
              description: "Show/hide territory icon tooltip in the Territories' legend",
            },
            {
              br: <br />,
              hidden: territory.territoryDetail.hidden,
              isMac: isMacOS(),
              limit: VISIBLE_TERRITORIES_LIMIT,
              showDisabled,
            }
          )}
        >
          <FontAwesomeIcon
            className={cn(styles.hideIcon, {
              [styles.hideIconDisabled]: showDisabled,
            })}
            icon={territory.territoryDetail.hidden ? faEyeSlash : faEye}
            onClick={handleToggleClick}
          />
        </Tooltip>
      </div>
    </div>
  );
};

export default memo(TerritoryItem);
